import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import {
  getAllTestimonials,
  getTestimonialById,
  updateTestimonial,
  deleteTestimonial,
  createTestimonial,
  getAllUsers,
} from "../../Utils/apiServices";
import Button from "../Button";
import Card from "../Card";
import { Typography } from "../Typography";
import toast from "../../Utils/toastManager";

// Canonical roles that can give a testimonial (must match the backend user role enum)
const ROLE_OPTIONS = [
  { value: "student", label: "Student" },
  { value: "mentor", label: "Mentor" },
  { value: "donor", label: "Donor" },
  { value: "admin", label: "Admin" },
  { value: "ecosystem_partner", label: "Ecosystem Partner" },
  { value: "corporate_sponsor", label: "Corporate Sponsor" },
  { value: "sponsor", label: "Sponsor" },
  { value: "investor", label: "Investor" },
  { value: "program_team", label: "Program Team" },
  { value: "volunteer", label: "Volunteer" },
];

const getRoleLabel = (role) => {
  if (!role) return "-";
  const match = ROLE_OPTIONS.find((r) => r.value === role);
  return match ? match.label : role.charAt(0).toUpperCase() + role.slice(1);
};

const TestimonialList = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState(""); // Role dependency filter
  const [formData, setFormData] = useState({
    message: "",
    user_id: "",
    role: "",
  });

  useEffect(() => {
    fetchTestimonials();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getAllUsers();
      if (response.success && response?.data?.data) {
        setUsers(
          Array.isArray(response.data?.data)
            ? response.data.data
            : []
        );
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await getAllTestimonials();
      if (response.success && response?.data?.data) {
        setTestimonials(
          Array.isArray(response.data?.data)
            ? response.data.data
            : []
        );
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // When a user is selected, default the role to that user's role (still editable)
  const handleUserChange = (value) => {
    const selectedUser = users.find(
      (u) => u.id?.toString() === value?.toString()
    );
    setFormData((prev) => ({
      ...prev,
      user_id: value,
      role: selectedUser?.role || prev.role,
    }));
  };

  // Filter users based on selected role
  const filteredUsers = useMemo(() => {
    if (!roleFilter) return users;
    return users.filter((user) => user.role === roleFilter);
  }, [users, roleFilter]);

  // Handle role filter change
  const handleRoleFilterChange = (selectedRole) => {
    setRoleFilter(selectedRole);
    // Reset user_id if current selected user doesn't match the new filter
    if (formData.user_id) {
      const currentUser = users.find((u) => u.id.toString() === formData.user_id.toString());
      if (currentUser && selectedRole && currentUser.role !== selectedRole) {
        setFormData((prev) => ({ ...prev, user_id: "" }));
      }
    }
  };

  const handleEdit = async (testimonial) => {
    try {
      // Fetch full testimonial details if needed
      const response = await getTestimonialById(testimonial.id);
      if (response.success && response.data?.data) {
        const testimonialData = response.data.data;
        const selectedUserId = testimonialData.user_id || testimonial.user_id;
        const selectedUser = users.find((u) => u.id === selectedUserId || u.id?.toString() === selectedUserId?.toString());
        
        setEditingId(testimonial.id);
        setShowCreateForm(false);
        // Set role filter based on the user's role
        setRoleFilter(selectedUser?.role || "");
        setFormData({
          message: testimonialData.message || "",
          user_id: selectedUserId || "",
          role: testimonialData.role || selectedUser?.role || "",
        });
      } else {
        // Fallback to the testimonial data we already have
        const selectedUserId = testimonial.user_id;
        const selectedUser = users.find((u) => u.id === selectedUserId || u.id?.toString() === selectedUserId?.toString());
        
        setEditingId(testimonial.id);
        setShowCreateForm(false);
        // Set role filter based on the user's role
        setRoleFilter(selectedUser?.role || "");
        setFormData({
          message: testimonial.message || "",
          user_id: selectedUserId || "",
          role: testimonial.role || selectedUser?.role || "",
        });
      }
    } catch (error) {
      console.error("Error fetching testimonial details:", error);
      // Fallback to the testimonial data we already have
      const selectedUserId = testimonial.user_id;
      const selectedUser = users.find((u) => u.id === selectedUserId || u.id?.toString() === selectedUserId?.toString());
      
      setEditingId(testimonial.id);
      setShowCreateForm(false);
      // Set role filter based on the user's role
      setRoleFilter(selectedUser?.role || "");
      setFormData({
        message: testimonial.message || "",
        user_id: selectedUserId || "",
        role: testimonial.role || selectedUser?.role || "",
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowCreateForm(false);
    setRoleFilter("");
    setFormData({
      message: "",
      user_id: "",
      role: "",
    });
  };

  const handleSave = async (testimonialId = null) => {
    try {
      const dataToSave = {
        message: formData.message,
        user_id: formData.user_id ? parseInt(formData.user_id, 10) : null,
        role: formData.role || null,
      };

      let response;
      if (testimonialId) {
        // Update existing testimonial
        response = await updateTestimonial(testimonialId, dataToSave);
      } else {
        // Create new testimonial
        if (!dataToSave.user_id) {
          toast.warning("Please select a user for this testimonial.");
          return;
        }
        response = await createTestimonial(dataToSave);
      }

      if (response.success) {
        await fetchTestimonials();
        handleCancelEdit();
      } else {
        toast.error(
          `Failed to ${testimonialId ? "update" : "create"} testimonial: ${
            response.error || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error(`Error saving testimonial:`, error);
      toast.error(`An error occurred while ${testimonialId ? "updating" : "creating"} the testimonial`);
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm("Are you sure you want to delete this testimonial?")) {
      return;
    }

    try {
      const response = await deleteTestimonial(testimonialId);
      if (response.success) {
        await fetchTestimonials();
      } else {
        toast.error("Failed to delete testimonial: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting testimonial:", error);
      toast.error("An error occurred while deleting the testimonial");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const getUserName = (userId) => {
    if (!userId) return "-";
    const user = users.find((u) => u.id === userId);
    if (user) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || `User ${userId}`;
    }
    return `User ${userId}`;
  };

  // Filter testimonials based on search term
  const filteredTestimonials = useMemo(() => {
    if (!searchTerm.trim()) return testimonials;
    
    const searchLower = searchTerm.toLowerCase();
    return testimonials.filter((testimonial) => {
      const message = (testimonial.message || "").toLowerCase();
      // Calculate userName inline to avoid dependency issues
      let userName = "-";
      if (testimonial.user_id) {
        const user = users.find((u) => u.id === testimonial.user_id || u.id?.toString() === testimonial.user_id?.toString());
        if (user) {
          userName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || `User ${testimonial.user_id}`;
        } else {
          userName = `User ${testimonial.user_id}`;
        }
      }
      
      return (
        message.includes(searchLower) ||
        userName.toLowerCase().includes(searchLower)
      );
    });
  }, [testimonials, searchTerm, users]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading testimonials...
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <Typography as="h2" className="text-2xl font-semibold text-primary-900">
          Testimonials
        </Typography>
        <Button
          onClick={() => {
            setShowCreateForm(true);
            setEditingId(null);
            setRoleFilter("");
            setFormData({
              message: "",
              user_id: "",
              role: "",
            });
          }}
          startIcon={<Icon icon="lucide:plus" className="h-4 w-4" />}
        >
          Create Testimonial
        </Button>
      </div>

      {showCreateForm && (
        <Card className="border border-slate-200 p-6">
          <div className="space-y-4">
            <Typography
              as="h3"
              className="text-base font-semibold text-primary-900 mb-4"
            >
              Create New Testimonial
            </Typography>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                  Filter by Role (Dependency)
                </label>
                <select
                  name="roleFilter"
                  value={roleFilter}
                  onChange={(e) => handleRoleFilterChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Roles</option>
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                  User <span className="text-red-500">*</span>
                </label>
                <select
                  name="user_id"
                  value={formData.user_id}
                  onChange={(e) => handleUserChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={filteredUsers.length === 0 && roleFilter !== ""}
                >
                  <option value="">
                    {filteredUsers.length === 0 && roleFilter !== ""
                      ? `No ${roleFilter} users found`
                      : "Select a user"}
                  </option>
                  {filteredUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || `User ${user.id}`} ({user.role})
                    </option>
                  ))}
                </select>
                {roleFilter && (
                  <p className="mt-1 text-xs text-tertiary-600">
                    Showing {filteredUsers.length} user(s) with role: {roleFilter}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select a role</option>
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-tertiary-600">
                  Role of the person giving this feedback (auto-filled from the selected user).
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) =>
                    handleInputChange("message", e.target.value)
                  }
                  placeholder="Enter testimonial message"
                  rows={6}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={() => handleSave()}>Create Testimonial</Button>
            </div>
          </div>
        </Card>
      )}

      <Card 
        className="border border-slate-200 overflow-hidden"
        header={
          <div className="flex items-center justify-end w-full">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-48 px-3 py-1.5 pl-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
              <Icon
                icon="lucide:search"
                className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400"
              />
            </div>
          </div>
        }
      >
        {testimonials.length === 0 ? (
          <div className="p-6 text-center">
            <Typography as="p" className="text-tertiary-600">
              No testimonials found.
            </Typography>
          </div>
        ) : (
          <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredTestimonials.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <Typography as="p" className="text-tertiary-600">
                        No testimonials found matching your search.
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  filteredTestimonials.map((testimonial) => (
                  <React.Fragment key={testimonial.id}>
                    {editingId === testimonial.id ? (
                      <tr className="bg-slate-50">
                        <td colSpan="5" className="px-6 py-4">
                          <div className="space-y-4">
                            <Typography
                              as="h3"
                              className="text-base font-semibold text-primary-900 mb-4"
                            >
                              Edit Testimonial
                            </Typography>
                            <div className="grid grid-cols-1 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Filter by Role (Dependency)
                                </label>
                                <select
                                  name="roleFilter"
                                  value={roleFilter}
                                  onChange={(e) => handleRoleFilterChange(e.target.value)}
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                  <option value="">All Roles</option>
                                  {ROLE_OPTIONS.map((role) => (
                                    <option key={role.value} value={role.value}>
                                      {role.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  User <span className="text-red-500">*</span>
                                </label>
                                <select
                                  name="user_id"
                                  value={formData.user_id}
                                  onChange={(e) => handleUserChange(e.target.value)}
                                  required
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  disabled={filteredUsers.length === 0 && roleFilter !== ""}
                                >
                                  <option value="">
                                    {filteredUsers.length === 0 && roleFilter !== ""
                                      ? `No ${roleFilter} users found`
                                      : "Select a user"}
                                  </option>
                                  {filteredUsers.map((user) => (
                                    <option key={user.id} value={user.id}>
                                      {`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || `User ${user.id}`} ({user.role})
                                    </option>
                                  ))}
                                </select>
                                {roleFilter && (
                                  <p className="mt-1 text-xs text-tertiary-600">
                                    Showing {filteredUsers.length} user(s) with role: {roleFilter}
                                  </p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Role <span className="text-red-500">*</span>
                                </label>
                                <select
                                  name="role"
                                  value={formData.role}
                                  onChange={(e) => handleInputChange("role", e.target.value)}
                                  required
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                  <option value="">Select a role</option>
                                  {ROLE_OPTIONS.map((role) => (
                                    <option key={role.value} value={role.value}>
                                      {role.label}
                                    </option>
                                  ))}
                                </select>
                                <p className="mt-1 text-xs text-tertiary-600">
                                  Role of the person giving this feedback (auto-filled from the selected user).
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Message <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                  name="message"
                                  value={formData.message}
                                  onChange={(e) =>
                                    handleInputChange("message", e.target.value)
                                  }
                                  placeholder="Enter testimonial message"
                                  rows={6}
                                  required
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-3">
                              <Button variant="secondary" onClick={handleCancelEdit}>
                                Cancel
                              </Button>
                              <Button onClick={() => handleSave(testimonial.id)}>
                                Save
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Typography
                            as="span"
                            className="text-sm font-medium text-primary-900"
                          >
                            {getUserName(testimonial.user_id)}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {testimonial.role ? (
                            <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                              {getRoleLabel(testimonial.role)}
                            </span>
                          ) : (
                            <Typography as="span" className="text-sm text-tertiary-600">
                              -
                            </Typography>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm text-tertiary-600 line-clamp-3"
                            title={testimonial.message}
                          >
                            {testimonial.message
                              ? testimonial.message.substring(0, 200)
                              : "-"}
                            {testimonial.message && testimonial.message.length > 200 && "..."}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900"
                          >
                            {formatDate(testimonial.created_at)}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleEdit(testimonial)}
                              startIcon={<Icon icon="lucide:edit" className="h-4 w-4" />}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleDelete(testimonial.id)}
                              startIcon={
                                <Icon icon="lucide:trash-2" className="h-4 w-4" />
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TestimonialList;
